import { formatDuration } from "modules/DealerReports/util/helper";
import { processField } from "modules/DealerReports/util/parser";

import type { ParsedReportData, ReportConfig } from "modules/DealerReports/util/parser";

const SNOOZE_CONFIG = {
  fields: [
    "location",
    "wo_count",
    "snooze_total",
    "no_department",
    "department_parts",
    "department_sales",
    "department_ccc",
    "department_bodyrepair",
    "department_external",
    "department_digital"
  ],
  calculations: {
    no_department: { totalField: "snooze_total" },
    department_parts: { totalField: "snooze_total" },
    department_sales: { totalField: "snooze_total" },
    department_ccc: { totalField: "snooze_total" },
    department_bodyrepair: { totalField: "snooze_total" },
    department_external: { totalField: "snooze_total" },
    department_digital: { totalField: "snooze_total" }
  },
  showTotals: true
};

export const REPORT_CONFIGS: Record<number, ReportConfig> = {
  1: {
    fields: [
      "location",
      "wo_count",
      "wo_geclaired",
      "check_count",
      "quality_checks_count",
      "quality_checks_percentage",
      "avg_qc_duration",
      "receptionist_check_count",
      "receptionist_check_percentage",
      "avg_receptionist_duration",
      "mechanic_check_count",
      "mechanic_check_percentage",
      "avg_mechanic_duration",
      "commercial_check_count",
      "commercial_check_percentage"
    ],
    calculations: {
      wo_geclaired: { totalField: "wo_count" },
      quality_checks_count: { totalField: "check_count", labelKey: "v8_quality_checks" },
      receptionist_check_count: { totalField: "check_count", labelKey: "v8_receptionist_check" },
      mechanic_check_count: { totalField: "check_count", labelKey: "v8_mechanic_check" },
      commercial_check_count: { totalField: "check_count", labelKey: "v8_check_commercial" }
    }
  },
  4: {
    fields: ["location", "total_cars", "recurring_cars", "recurring_cars_not_worked_on"],
    calculations: {
      recurring_cars: { totalField: "total_cars" }
    }
  },
  5: {
    fields: [
      "location",
      "wo_count",
      "commercial_check_count",
      "avg_advice_point_count",
      "avg_advice_point_count_yes",
      "avg_critical_point_count",
      "avg_critical_point_count_yes",
      "avg_sold_items",
      "percent_sold_items",
      "avg_photo_taken",
      "avg_mechanic_time",
      "avg_offer_ready",
      "avg_customer_ok"
    ],
    dynamicFields: {
      pattern: "",
      customProcessor: (result, row) => {
        const { avg_receptionist_time: _avgReceptionistTime, ...newResult } = result;
        let avg_offer_ready = 0;
        let avg_customer_ok = 0;

        try {
          if (row.avg_receptionist_time) {
            const parsed = JSON.parse(row.avg_receptionist_time);
            avg_offer_ready = parsed.avg_offer_ready || 0;
            avg_customer_ok = parsed.avg_customer_ok || 0;
          }
        } catch (error) {
          console.error("Failed to parse data", error);
        }

        return {
          ...newResult,
          avg_offer_ready: formatDuration(avg_offer_ready),
          avg_customer_ok: formatDuration(avg_customer_ok)
        };
      }
    }
  },
  7: {
    fields: [
      "location",
      "wo_count",
      "answered_desk_checkin_wo_percent",
      "online_checkin_wo_percent",
      "opened_online_checkin",
      "answered_online_checkin",
      "answered_checkin_wo_percent",
      "diagnose_overview_wo_percent",
      "opened_diagnose_overview",
      "answered_diagnose_overview",
      "repair_overview_wo_percent",
      "opened_repair_overview"
    ],
    calculations: {
      answered_desk_checkin_wo_percent: {
        countField: "answered_desk_checkin_wo_count",
        percentField: "answered_desk_checkin_wo_percent",
        labelKey: "v8_desk_checkin_answered"
      },
      online_checkin_wo_percent: { countField: "online_checkin_wo_count", percentField: "online_checkin_wo_percent", labelKey: "v8_online_checkin" },
      opened_online_checkin: { countField: "opened_online_checkin", percentField: "opened_online_checkin_percent" },
      answered_online_checkin: { countField: "answered_online_checkin", percentField: "answered_online_checkin_percent" },
      answered_checkin_wo_percent: { countField: "answered_checkin_wo_count", percentField: "answered_checkin_wo_percent", labelKey: "v8_total_check_in_answered" },
      diagnose_overview_wo_percent: { countField: "diagnose_overview_wo_count", percentField: "diagnose_overview_wo_percent", labelKey: "v8_diagnose_overview" },
      opened_diagnose_overview: { countField: "opened_diagnose_overview", percentField: "opened_diagnose_overview_percent" },
      answered_diagnose_overview: { countField: "answered_diagnose_overview", percentField: "answered_diagnose_overview_percent" },
      repair_overview_wo_percent: { countField: "repair_overview_wo_count", percentField: "repair_overview_wo_percent", labelKey: "v8_repair_overview" },
      opened_repair_overview: { countField: "opened_repair_overview", percentField: "opened_repair_overview_percent" }
    }
  },
  8: {
    fields: ["location", "services_count", "services_accepted"],
    dynamicFields: {
      pattern: "",
      customProcessor: (result, row, config) => {
        const orderedResult = new Map();

        config.fields.forEach(key => {
          orderedResult.set(key, result[key]);
        });

        const dynamicFields = Object.keys(row)
          .filter(key => !config.fields.includes(key) && key !== "Others")
          .sort((a, b) => a.localeCompare(b));

        dynamicFields.forEach(key => {
          orderedResult.set(key, {
            value: processField(row[key], key),
            params: {
              tag: key,
              translationKey: "tag_accepted",
              hideOnUi: false,
              hideOnExport: false
            }
          });
        });

        if ("Others" in row) orderedResult.set("Others", processField(row.Others, "Others"));
        return Object.fromEntries(orderedResult);
      }
    },
    showTotals: false
  },
  11: {
    fields: ["location", "wo_count", "canceled_count"],
    calculations: {
      canceled_count: { countField: "canceled_count", percentField: "canceled_percent", labelKey: "v8_canceled" }
    },
    dynamicFields: {
      pattern: "cancel_reason_",
      customProcessor: (result, row, config) => {
        const orderedResults = new Map();

        config.fields.forEach(key => {
          orderedResults.set(key, result[key]);
        });

        const cancelReasonFields = Object.keys(row).filter(key => key.startsWith("cancel_reason_"));

        cancelReasonFields.forEach(key => {
          orderedResults.set(key, processField(row[key], key));
        });

        if ("custom_reason_other" in row) orderedResults.set("custom_reason_other", processField(row.custom_reason_other, "custom_reason_other"));

        return Object.fromEntries(orderedResults);
      }
    }
  },
  12: {
    fields: [
      "location",
      "wo_count",
      "wo_lease_count",
      "wo_geclaired",
      "wo_commercial_count",
      "check_count",
      "commercial_check_count",
      "avg_advice_point_count",
      "avg_advice_point_count_yes",
      "percent_advice_sold_items",
      "avg_critical_point_count",
      "avg_critical_point_count_yes",
      "percent_critical_sold_items",
      "percent_sold_items"
    ],
    calculations: {
      wo_lease_count: { totalField: "wo_count", labelKey: "v8_wo_lease" },
      wo_geclaired: { totalField: "wo_lease_count", colorType: "geclaired" },
      wo_commercial_count: { totalField: "wo_geclaired", labelKey: "v8_wo_commercial" },
      commercial_check_count: { totalField: "check_count", labelKey: "v8_check_commercial" },
      avg_advice_point_count: {
        totalCalculation: totals => (totals.advice_point_count_no + totals.advice_point_count_yes) / totals.commercial_check_count
      },
      avg_advice_point_count_yes: {
        totalCalculation: totals => totals.advice_point_count_yes / totals.commercial_check_count
      },
      percent_advice_sold_items: {
        totalCalculation: totals => (totals.advice_point_count_yes / (totals.advice_point_count_no + totals.advice_point_count_yes)) * 100
      },
      avg_critical_point_count: {
        totalCalculation: totals => (totals.critical_point_count_no + totals.critical_point_count_yes) / totals.commercial_check_count
      },
      avg_critical_point_count_yes: {
        totalCalculation: totals => totals.critical_point_count_yes / totals.commercial_check_count
      },
      percent_critical_sold_items: {
        totalCalculation: totals => (totals.critical_point_count_yes / (totals.critical_point_count_no + totals.critical_point_count_yes)) * 100
      },
      percent_sold_items: {
        totalCalculation: totals => ((totals.advice_point_count_yes + totals.critical_point_count_yes) / totals.point_count) * 100
      }
    },
    showTotals: true
  },
  13: {
    fields: ["location", "wo_count", "snooze_total", "next_visit_customer", "make_offer", "make_new_appointment", "remind_customer"],
    calculations: {
      next_visit_customer: { totalField: "snooze_total" },
      make_offer: { totalField: "snooze_total" },
      make_new_appointment: { totalField: "snooze_total" },
      remind_customer: { totalField: "snooze_total" }
    },
    showTotals: true
  },
  14: SNOOZE_CONFIG,
  15: SNOOZE_CONFIG,
  16: SNOOZE_CONFIG,
  17: SNOOZE_CONFIG,
  18: {
    fields: ["location", "wo_count", "key_drop_check_in", "key_drop_phone_number", "key_drop_reg_number", "key_drop_for_appointment", "key_drop_back_for_appointment"],
    calculations: {
      key_drop_check_in: { countField: "key_drop_check_in", percentField: "key_drop_check_in_percent" },
      key_drop_phone_number: { countField: "key_drop_phone_number", percentField: "key_drop_phone_number_percent" },
      key_drop_reg_number: { countField: "key_drop_reg_number", percentField: "key_drop_reg_number_percent" },
      key_drop_for_appointment: { countField: "key_drop_for_appointment", percentField: "key_drop_for_appointment_percent", labelKey: "v8_key_dropped" },
      key_drop_back_for_appointment: { countField: "key_drop_back_for_appointment", percentField: "key_drop_back_for_appointment_percent" }
    }
  },
  19: {
    fields: ["location", "wo_count", "sms_sent", "average_sms_per_wo"],
    calculations: {
      sms_sent: { countField: "sms_sent", percentField: "sms_sent_percent" }
    }
  },
  21: {
    fields: ["location", "wo_count", "sharebox_drop_count", "sharebox_drop_back_count"],
    calculations: {
      sharebox_drop_count: { countField: "sharebox_drop_count", percentField: "sharebox_drop_percent", labelKey: "v8_keylocker_sharebox_key_dropped" },
      sharebox_drop_back_count: { countField: "sharebox_drop_back_count", percentField: "sharebox_drop_back_percent", labelKey: "v8_keylocker_sharebox_key_dropped_back" }
    }
  },
  20: {
    fields: [
      "location",
      "wo_count",
      "online_checkin_sms_sent_count",
      "online_checkin_sms_sent_percentage",
      "online_checkin_sms_confirmation_count",
      "online_checkin_sms_confirmation_percentage",
      "keylocker_checkin_sms_sent_count",
      "keylocker_checkin_sms_sent_percentage",
      "diagnose_overview_sms_confirmation_count",
      "diagnose_overview_sms_confirmation_percentage",
      "diagnose_overview_sms_sent_count",
      "diagnose_overview_sms_sent_percentage",
      "repair_overview_sms_sent_count",
      "repair_overview_sms_sent_percentage"
    ],
    dynamicFields: {
      pattern: "",
      customProcessor: result => {
        const { location, wo_count, ...rest } = result;
        const customFields: ParsedReportData = {};
        const initialDataKeys = Object.keys(rest);

        initialDataKeys.forEach(key => {
          const value = rest[key];

          if (typeof value === "number") {
            customFields[key] = {
              value,
              ...(key.includes("_count") ? {} : { percent: value }),
              params: { hideOnUi: true }
            };
          }
        });

        const uiFields = [
          "online_checkin_sms_sent",
          "online_checkin_sms_confirmation",
          "keylocker_checkin_sms_sent",
          "diagnose_overview_sms_confirmation",
          "diagnose_overview_sms_sent",
          "repair_overview_sms_sent"
        ];

        uiFields.forEach(field => {
          const [countField, percentField] = initialDataKeys.filter(key => key.includes(field)).sort();
          if (typeof rest[countField] === "number" && typeof rest[percentField] === "number") {
            customFields[field] = { value: rest[countField], percent: rest[percentField], params: { hideOnExport: true } };
          }
        });

        return { location, wo_count, ...customFields };
      }
    }
  },
  22: {
    fields: ["location", "offered", "mounted"]
  },
  23: {
    fields: ["location", "offered", "ordered", "mounted"]
  },
  24: {
    fields: [
      "user_location",
      "user_name",
      "user_role",
      "wo_location",
      "wo_handled",
      "check_aanname",
      "check_commercial",
      "checks_found_items",
      "check_media",
      "check_not_commercial",
      "check_extra_parts",
      "check_warranty",
      "check_count",
      "status_calculating",
      "status_pricing_ok",
      "status_info",
      "status_customer_ok",
      "status_car_ready",
      "status_quality",
      "cc_extra_services",
      "cc_diagnose_overview",
      "cc_repair_overview",
      "dc_checkin",
      "dc_checkout"
    ]
  },
  25: {
    fields: ["location", "wo_count", "started", "completed", "remarks"]
  },
  27: {
    fields: [
      "location",
      "wo_count",
      "wo_count_worked_on",
      "wo_count_completed",
      "wo_commercial_count",
      "check_count",
      "commercial_check_count",
      "average_advised_items",
      "average_advised_interventions",
      "average_advised_sold",
      "percent_advised_sold",
      "average_advised_fixed",
      "average_critical_items",
      "average_critical_interventions",
      "average_critical_sold",
      "percent_critical_sold",
      "average_critical_fixed",
      "average_mechanic_check_time",
      "average_mechanic_photo_taken"
    ],
    calculations: {
      wo_count_worked_on: { totalField: "wo_count" },
      wo_count_completed: { totalField: "wo_count_worked_on" },
      wo_commercial_count: { totalField: "wo_count", labelKey: "v8_wo_commercial" },
      commercial_check_count: { totalField: "check_count", labelKey: "v8_check_commercial" }
    },
    dynamicFields: {
      pattern: "average_mechanic_check_time",
      customProcessor: (result, row) => {
        if (typeof row.average_mechanic_check_time === "string") result.average_mechanic_check_time = row.average_mechanic_check_time.split(".")[0];

        return result;
      }
    }
  },
  29: {
    fields: [
      "location",
      "wo_count",
      "wo_commercial_count",
      "commercial_check_count",
      "average_advised_items",
      "average_advised_interventions",
      "average_advised_sold",
      "percent_advised_sold",
      "average_advised_fixed",
      "average_critical_items",
      "average_critical_interventions",
      "average_critical_sold",
      "percent_critical_sold",
      "average_critical_fixed",
      "average_mechanic_check_time",
      "average_mechanic_photo_taken"
    ],
    calculations: {
      wo_commercial_count: { percentField: "wo_commercial_count_percent", labelKey: "v8_wo_commercial" },
      commercial_check_count: { percentField: "commercial_check_count_percent", labelKey: "v8_check_commercial" }
    },
    dynamicFields: {
      pattern: "average_mechanic_check_time",
      customProcessor: (result, row) => {
        if (typeof row.average_mechanic_check_time === "string") result.average_mechanic_check_time = row.average_mechanic_check_time.split(".")[0];
        return result;
      }
    }
  }
};
